/**
 * useThriveImport — the New Import wizard's Thrive path (epic
 * 2026-10-15-thrive-search-import, sub-task 03).
 *
 * Three steps after Source: the SOURCE search (any status), the stages to
 * bring, and the TARGET search the list is for (Open + BD, as on upload),
 * which carries the Import button (the Preview step was dropped on Jared's
 * feedback, 2026-10-20). Submit is `POST /imports/thrive-search`; the server mints the
 * list in `parsing` and the wizard opens it, where the detail page's existing
 * poll takes over (access pattern 3, like an upload's `/complete`).
 *
 * The stage counts are a live Thrive read (`GET /searches/{id}/thrive-preview`,
 * sub-task 01), started as soon as a source is picked so the Stages step is
 * usually ready when the recruiter gets there. Every stage starts checked: the
 * hook stores the UNCHECKED ones, so a new source starts with all of its
 * stages and a stage that appears on a refetch arrives checked.
 *
 * The stages are always sent explicitly, never `null`: `null` means "every
 * stage at import time", which is for API callers. The recruiter chose from a
 * list, and a stage that appeared in Thrive since they looked was not on it.
 */

import type { ThrivePreviewResponse } from "@nu-assist/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ImportsApiError, type ImportsClient } from "../../../clients/imports.client";
import type { SearchTypeaheadOption } from "./steps/SearchTypeahead";

export type ThriveStep = "search" | "stages" | "target";

export const THRIVE_STEPS: readonly ThriveStep[] = ["search", "stages", "target"];

/** The list title's limit, as `ThriveSearchImportRequest.title` caps it. */
export const THRIVE_IMPORT_TITLE_MAX = 200;

export interface ThriveTarget {
  id: string;
  name: string;
}

/** How a failed preview read is told to the recruiter. */
export interface PreviewFailure {
  message: string;
  /** False when asking again repeats the same answer (a deadline, a refusal). */
  canRetry: boolean;
  /** Set for a search too large to count live: its dashboard composes the
   *  report cache the preview can then read. */
  openDashboard: boolean;
}

export function describePreviewFailure(err: unknown): PreviewFailure {
  if (err instanceof ImportsApiError) {
    if (err.status === 503 && err.detailData?.reason === "deadline") {
      return {
        message:
          "This search is too large to count live. Open its dashboard once to build its counts, then come back.",
        canRetry: false,
        openDashboard: true,
      };
    }
    if (err.status === 503) {
      return {
        message: "Thrive is busy right now. Try again in a moment.",
        canRetry: true,
        openDashboard: false,
      };
    }
    if (err.status === 502 && err.detailData?.retryable === false) {
      return {
        message: "Thrive refused to read this search. Trying again will not help.",
        canRetry: false,
        openDashboard: false,
      };
    }
    if (err.status === 404) {
      return {
        message: "This search cannot be imported here.",
        canRetry: false,
        openDashboard: false,
      };
    }
  }
  return {
    message: "Couldn’t read this search from Thrive.",
    canRetry: true,
    openDashboard: false,
  };
}

/** The counts equal what the import brings in only when they were read live
 *  and Thrive's pages added up (sub-task 01's `counts_source` /
 *  `counts_approximate`). */
export function countsAreExact(preview: ThrivePreviewResponse): boolean {
  return preview.counts_source === "live" && !preview.counts_approximate;
}

export const SUBMIT_FAILED_MESSAGE = "Couldn’t start the import. Try again.";

export interface UseThriveImportResult {
  step: ThriveStep;
  goNext: () => void;
  goBack: () => void;
  source: SearchTypeaheadOption | null;
  chooseSource: (option: SearchTypeaheadOption | null) => void;
  title: string;
  setTitle: (title: string) => void;
  preview: ThrivePreviewResponse | undefined;
  previewLoading: boolean;
  previewFailure: PreviewFailure | null;
  retryPreview: () => void;
  /** Checked stages, in the preview's ladder order. */
  checkedStages: string[];
  isChecked: (stage: string) => boolean;
  toggleStage: (stage: string) => void;
  /** Sum of the checked stages' counts: the N of "Import N candidacies". */
  checkedCount: number;
  target: ThriveTarget | null;
  setTarget: (target: ThriveTarget | null) => void;
  submit: () => void;
  submitting: boolean;
  submitError: string | null;
  /** Why Next (or Import, on the last step) is disabled; `null` when enabled. */
  blockedReason: string | null;
}

export function useThriveImport(client: ImportsClient): UseThriveImportResult {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<ThriveStep>("search");
  const [source, setSource] = useState<SearchTypeaheadOption | null>(null);
  const [title, setTitle] = useState("");
  // The last title the wizard wrote itself, so a re-pick replaces it but
  // never a title the recruiter typed.
  const [prefilledTitle, setPrefilledTitle] = useState("");
  const [unchecked, setUnchecked] = useState<ReadonlySet<string>>(new Set());
  const [target, setTarget] = useState<ThriveTarget | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const sourceId = source?.id ?? null;
  const previewQuery = useQuery({
    queryKey: ["thrive-preview", sourceId],
    queryFn: () => client.getThrivePreview(sourceId as string),
    enabled: sourceId !== null,
    // The API already retried a 429 once inside its 45 s deadline; a client
    // retry on top would hold the step on a spinner for minutes.
    retry: false,
    staleTime: 5 * 60_000,
  });
  const preview = previewQuery.data;
  const stages = preview?.stages ?? [];
  const checkedStages = stages.map((s) => s.stage).filter((stage) => !unchecked.has(stage));
  const checkedCount = stages
    .filter((s) => !unchecked.has(s.stage))
    .reduce((sum, s) => sum + s.count, 0);

  function chooseSource(option: SearchTypeaheadOption | null) {
    setSource(option);
    setUnchecked(new Set());
    // The title follows the SOURCE (the target step does not change it), and
    // only while the recruiter has not typed their own.
    if (option && (title === "" || title === prefilledTitle)) {
      const next = option.name.slice(0, THRIVE_IMPORT_TITLE_MAX);
      setTitle(next);
      setPrefilledTitle(next);
    }
  }

  function toggleStage(stage: string) {
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  }

  function reasonFor(current: ThriveStep): string | null {
    if (current === "search") {
      if (!source) return "Pick the Thrive search to pull from.";
      if (!title.trim()) return "Name the new list.";
      return null;
    }
    if (current === "stages") {
      if (!preview) return previewQuery.isError ? "The stages could not be read." : "Counting…";
      if (preview.total === 0) return "This search has no candidacies to import.";
      if (checkedStages.length === 0) return "Choose at least one stage to import.";
      return null;
    }
    if (current === "target") {
      return target ? null : "Pick the search this list is for.";
    }
    return null;
  }

  const mutation = useMutation({
    mutationFn: () =>
      client.createThriveSearchImport({
        source_search_id_thrive: sourceId as string,
        target_search_id_thrive: (target as ThriveTarget).id,
        title: title.trim(),
        stages: checkedStages,
      }),
    onSuccess: (accepted) => {
      // As on upload: the firm-wide owner set may have just changed.
      void queryClient.invalidateQueries({ queryKey: ["imports", "owners"] });
      navigate(`/research/imports/${accepted.import_id}`);
    },
    onError: (err) => {
      setSubmitError(
        err instanceof ImportsApiError && typeof err.detail === "string"
          ? err.detail
          : SUBMIT_FAILED_MESSAGE
      );
    },
  });

  const index = THRIVE_STEPS.indexOf(step);
  const blockedReason = reasonFor(step);

  return {
    step,
    goNext: () => {
      if (blockedReason === null && index < THRIVE_STEPS.length - 1) {
        setStep(THRIVE_STEPS[index + 1]);
      }
    },
    goBack: () => {
      if (index > 0) setStep(THRIVE_STEPS[index - 1]);
    },
    source,
    chooseSource,
    title,
    setTitle,
    preview,
    previewLoading: previewQuery.isPending && sourceId !== null,
    previewFailure: previewQuery.isError ? describePreviewFailure(previewQuery.error) : null,
    retryPreview: () => void previewQuery.refetch(),
    checkedStages,
    isChecked: (stage) => !unchecked.has(stage),
    toggleStage,
    checkedCount,
    target,
    setTarget,
    submit: () => {
      if (!source || !target || checkedStages.length === 0 || mutation.isPending) return;
      setSubmitError(null);
      mutation.mutate();
    },
    submitting: mutation.isPending,
    submitError,
    blockedReason,
  };
}
